import ValidatePhaseDeployment from './ValidatePhaseDeployment';
import ValidatePhaseValidation from './ValidatePhaseValidation';
import ValidatePhaseReadout from './ValidatePhaseReadout';
import ValidatePhasePoV from './ValidatePhasePoV';
import {
  HbrOption,
  HbrSelect,
  HbrTab,
  HbrTabGroup,
  HbrTabPanel,
} from '@harbor/elements-react-standalone';
import ValidatePhaseQualification from './ValidatePhaseQualification';
import {
  OpportunityProduct,
} from '../../../../types/opportunity';
import {
  ValidationMethod,
  ValidationSection,
} from '../../../../types/ValidatePhaseTypes';
import { isPovStarted } from '../../opportunityDetailHelpers';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useFetchOpportunity,
  useFetchOpportunityProductAssignment,
} from '../../../../hooks/opportunity-hooks';
import { useFetchSingleProduct } from '../../../../hooks/product-hooks';
import ValidatePhaseTestCases from './ValidatePhaseTestCases';
import { TestCaseFormProvider } from './ValidationTestCaseForm/ValidationTestCaseFormContext';
import { asSingleValue } from '../../../../support/utils';
import OpportunityPhaseLayout from '../OpportunityPhaseLayout';
import OpportunityPhaseCard from '../OpportunityPhaseCard';

interface ValidateTabState {
  disabled: boolean;
  visible: boolean;
}

const ValidatePhase = () => {
  const { id: opportunityId } = useParams();
  useFetchOpportunity(opportunityId, { enabled: !!opportunityId });

  const [povProducts, setPovProducts] = useState<OpportunityProduct[]>([]);
  const [selectedSection, setSelectedSection] = useState<ValidationSection>(
    ValidationSection.Qualification
  );
  const [selectedProductId, setSelectedProductId] = useState<number>(null);

  const { data: selectedProduct } = useFetchOpportunityProductAssignment(
    selectedProductId,
    opportunityId,
    { enabled: !!opportunityId && !!selectedProductId }
  );

  const selectedBaseProductId = selectedProduct?.product?.id;

  const { data: detailedProduct } = useFetchSingleProduct(
    selectedBaseProductId,
    undefined,
    { enabled: !!selectedBaseProductId }
  );

  const selectedProductWithCatalog = useMemo(() => {
    if (!selectedProduct) return null;

    return {
      ...selectedProduct,
      product: detailedProduct || selectedProduct.product,
    };
  }, [selectedProduct, detailedProduct]);

  const tabIsVisible = (section: ValidationSection): boolean => {
    return (
      section === ValidationSection.Qualification ||
      (selectedProduct &&
        (selectedProduct?.validation_method === ValidationMethod.PoV ||
          ![ValidationSection.PoV, ValidationSection.Readout].includes(section)))
    );
  };

  const tabIsDisabled = (section: ValidationSection): boolean => {
    return (
      [
        ValidationSection.Deployment,
        ValidationSection.Validation,
        ValidationSection.Readout,
      ].includes(section) &&
      !isPovStarted(selectedProduct) &&
      selectedProduct?.validation_method === ValidationMethod.PoV
    );
  };

  const tabStates: { [key: string]: ValidateTabState } = useMemo(() => {
    const sections = [
      ValidationSection.Qualification,
      ValidationSection.PoV,
      ValidationSection.TestCases,
      ValidationSection.Deployment,
      ValidationSection.Validation,
      ValidationSection.Readout,
    ];

    return sections.reduce((acc, section) => {
      acc[section] = {
        disabled: tabIsDisabled(section),
        visible: tabIsVisible(section),
      };
      return acc;
    }, {} as { [key: string]: ValidateTabState });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  const handlePovProductSelect = (name: string): void => {
    const found = povProducts.find(
      (p) => String(p.product.name) === String(name)
    );
    if (found) {
      setSelectedProductId(found.id);
      if (selectedSection === ValidationSection.Qualification) {
        setSelectedSection(
          found?.validation_method === ValidationMethod.PoV
            ? ValidationSection.PoV
            : ValidationSection.TestCases
        );
      }
    }
  };

  const handleSectionNavigationClick = (section: ValidationSection) => {
    if (section === ValidationSection.Qualification) {
      setSelectedProductId(null);
    }
    if (!tabStates[section].disabled) {
      setSelectedSection(section);
    }
  };

  useEffect(() => {
    if (selectedProduct) {
      if (selectedProduct.validation_method !== ValidationMethod.PoV) {
        if (selectedSection === ValidationSection.PoV) {
          setSelectedSection(ValidationSection.TestCases);
        }
        if (selectedSection === ValidationSection.Readout) {
          setSelectedSection(ValidationSection.Validation);
        }
      }
    }
  }, [selectedProduct, selectedSection]);

  return (
    <OpportunityPhaseLayout
      title="Validate Phase"
      subtitle="Qualify the engagement, define the PoV, and capture validation progress."
      toolbar={
        <div className="flex justify-end">
          <div className="w-full max-w-xs">
            <HbrSelect
              placeholder="PoV Products"
              value={selectedProduct?.product?.name}
              onHbr-change={(e) => handlePovProductSelect(asSingleValue(e.target.value))}
              className="w-full"
            >
              {(povProducts ?? []).map((option) => (
                <HbrOption key={option.id} value={option?.product?.name}>
                  {option?.product?.name}
                </HbrOption>
              ))}
            </HbrSelect>
          </div>
        </div>
      }
    >
      <OpportunityPhaseCard bodyClassName="p-4">
        <HbrTabGroup primary>
          {Object.keys(tabStates)
            .filter((section) => tabStates[section].visible)
            .map((section) => (
              <HbrTab
                key={section}
                slot="nav"
                panel={section}
                active={selectedSection === section}
                onClick={() =>
                  handleSectionNavigationClick(section as ValidationSection)
                }
                disabled={tabStates[section].disabled}
              >
                {section}
              </HbrTab>
            ))}

          <div className="h-4"></div>

          <HbrTabPanel
            name={ValidationSection.Qualification}
            active={selectedSection === ValidationSection.Qualification}
          >
            <ValidatePhaseQualification
              setPovProducts={setPovProducts}
              handlePovProductSelect={handlePovProductSelect}
            />
          </HbrTabPanel>

          {selectedSection === ValidationSection.PoV && (
            <HbrTabPanel
              name={ValidationSection.PoV}
              active={selectedSection === ValidationSection.PoV}
            >
              <ValidatePhasePoV selectedProductId={selectedProductId} />
            </HbrTabPanel>
          )}

          {selectedSection === ValidationSection.TestCases && (
            <HbrTabPanel
              name={ValidationSection.TestCases}
              active={selectedSection === ValidationSection.TestCases}
            >
              <TestCaseFormProvider
                opportunityProduct={selectedProductWithCatalog}
              >
                <ValidatePhaseTestCases
                  selectedProductId={selectedProductId}
                  isActive={selectedSection === ValidationSection.TestCases}
                />
              </TestCaseFormProvider>
            </HbrTabPanel>
          )}

          {selectedSection === ValidationSection.Deployment && (
            <HbrTabPanel
              name={ValidationSection.Deployment}
              active={selectedSection === ValidationSection.Deployment}
            >
              <ValidatePhaseDeployment selectedProductId={selectedProductId} />
            </HbrTabPanel>
          )}

          {selectedSection === ValidationSection.Validation && (
            <HbrTabPanel
              name={ValidationSection.Validation}
              active={selectedSection === ValidationSection.Validation}
            >
              <ValidatePhaseValidation selectedProductId={selectedProductId} />
            </HbrTabPanel>
          )}

          {selectedSection === ValidationSection.Readout && (
            <HbrTabPanel
              name={ValidationSection.Readout}
              active={selectedSection === ValidationSection.Readout}
            >
              <ValidatePhaseReadout selectedProductId={selectedProductId} />
            </HbrTabPanel>
          )}
        </HbrTabGroup>
      </OpportunityPhaseCard>
    </OpportunityPhaseLayout>
  );
};

export default ValidatePhase;